import React, { FC, useCallback, useMemo } from 'react';
import { CustomButton, FormContainer } from '@ui/components/atoms';
import { IFormItemType } from '@interfaces/FormItemType';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SubFormSection } from '@ui/components/molecules/SubFormSection';
import ArrowIcon from '@assets/svg/arrow.svg';
import { useDataSubjectRightVerifyOtp } from '@services/DataSubjectRight';
import { useTranslate } from '@translations/useTranslate';

export const OtpForm: FC<{
  isEmail: boolean;
  verificationText: string;
  requestId: string;
  onSubmit: (jiraTicket: string) => void;
}> = ({ isEmail, verificationText, requestId, onSubmit }) => {
  const { t } = useTranslate();
  const [error, setError] = React.useState<string | undefined>();
  const { mutateAsync, isLoading } = useDataSubjectRightVerifyOtp();
  const maskEmail = useCallback((email: string) => {
    const [localPart, domainPart] = email.split('@');
    const unmaskedPart = localPart.slice(-3);
    return `xxxxx${unmaskedPart}@${domainPart}`;
  }, []);
  const maskMobile = useCallback((mobile: string) => {
    const maskLength = mobile.length - 4;
    const maskedPart = 'x'.repeat(maskLength > 0 ? maskLength : 0);
    const unmaskedPart = mobile.slice(-4);
    return `${maskedPart}${unmaskedPart}`;
  }, []);
  const otpFormScheme = Yup.object().shape({
    otp: Yup.string()
      .min(6, t('global.error.invalidInput'))
      .max(6, t('global.error.invalidInput'))
      .required(t('global.error.required')),
  });
  const formik = useFormik({
    initialValues: {
      otp: '',
    },
    onSubmit: (values) => {
      mutateAsync({ ...values, requestId })
        .then(({ jiraTicketId }) => {
          onSubmit(jiraTicketId);
        })
        .catch((e) => {
          setError(e.errorCode === '400' ? t('global.error.invalidOtp') : t('global.error'));
        });
    },
    validationSchema: otpFormScheme,
    validateOnChange: false,
    validateOnBlur: true,
  });
  const formItems: IFormItemType[] = useMemo(() => {
    let maskedVerificationText = '';
    if (isEmail) {
      maskedVerificationText = maskEmail(verificationText);
    } else {
      maskedVerificationText = maskMobile(verificationText);
    }
    return [
      {
        id: 'otp',
        label: `${t('otp.form.label')} ${maskedVerificationText}`,
        placeholder: t('otp.form.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.otp,
        isTouched: formik.touched.otp,
        isValid: !formik.errors.otp,
        errorFeedback: formik.errors.otp,
        onChange: (value) => formik.setFieldValue('otp', value),
        onBlur: () => formik.setFieldTouched('otp', true),
        isCenter: true,
      },
    ];
  }, [formik, isEmail, maskEmail, maskMobile, verificationText, t]);
  return (
    <FormContainer error={error}>
      <SubFormSection items={formItems} />
      <div className="w-100 pt-3 pb-5 d-flex justify-content-center align-items-center gap-3">
        <CustomButton
          variant="primary"
          text={t('submit')}
          disabled={isLoading}
          suffixIcon={ArrowIcon}
          onClick={formik.handleSubmit}
        />
        <CustomButton
          variant="outline-dark"
          text={t('cancel')}
          onClick={() => {
            window.location.reload();
          }}
        />
      </div>
    </FormContainer>
  );
};
